// ─── Dummy data types & fixtures for Consultation module ───

export type FileKind = 'image' | 'document';
export type FileTag = 'images' | 'documents' | 'consent' | 'lab' | 'before_after' | 'other';

export interface PatientFileItem {
  id: string;
  patientId: string;
  kind: FileKind;
  filename: string;
  url?: string;
  tag: FileTag;
  createdAt: string;
  starred: boolean;
  mimeType?: string;
  description?: string;
}

export interface PatientNoteItem {
  id: string;
  patientId: string;
  title: string;
  body: string;
  updatedAt: string;
}

export interface PatientFormItem {
  id: string;
  patientId: string;
  name: string;
  tag: 'consent' | 'lab' | 'other';
  createdAt: string;
}

// ─── Filter chip labels ───

export const FILE_FILTER_CHIPS = [
  'All',
  'Favorites',
  'Images',
  'Documents',
  'Consent',
  'Lab',
  'Before/After',
  'Other',
] as const;

export type FileFilterChip = (typeof FILE_FILTER_CHIPS)[number];

export const chipToTag: Record<FileFilterChip, FileTag | null> = {
  All: null,
  Favorites: null,
  Images: 'images',
  Documents: 'documents',
  Consent: 'consent',
  Lab: 'lab',
  'Before/After': 'before_after',
  Other: 'other',
};

// ─── Dummy image files ───

export function getDummyFiles(patientId: string): PatientFileItem[] {
  return [
    {
      id: 'f1',
      patientId,
      kind: 'image',
      filename: 'dental-impression.jpg',
      url: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=300&fit=crop',
      tag: 'other',
      createdAt: '2026-02-18T10:40:00Z',
      starred: false,
    },
    {
      id: 'f2',
      patientId,
      kind: 'image',
      filename: 'intraoral-examination.jpg',
      url: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=300&fit=crop',
      tag: 'other',
      createdAt: '2026-02-18T10:35:00Z',
      starred: false,
    },
    {
      id: 'f3',
      patientId,
      kind: 'image',
      filename: 'teeth-before-whitening.jpg',
      url: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=400&h=300&fit=crop',
      tag: 'before_after',
      createdAt: '2026-02-18T10:30:00Z',
      starred: false,
    },
    {
      id: 'f4',
      patientId,
      kind: 'image',
      filename: 'panoramic-xray.jpg',
      url: 'https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=400&h=300&fit=crop',
      tag: 'lab',
      createdAt: '2026-02-17T14:20:00Z',
      starred: false,
    },
    {
      id: 'f5',
      patientId,
      kind: 'image',
      filename: 'crown-placement-after.jpg',
      url: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=400&h=300&fit=crop',
      tag: 'before_after',
      createdAt: '2026-02-16T09:15:00Z',
      starred: true,
    },
    {
      id: 'f6',
      patientId,
      kind: 'image',
      filename: 'periapical-radiograph.jpg',
      url: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop',
      tag: 'lab',
      createdAt: '2026-02-15T11:00:00Z',
      starred: true,
    },
    // Documents
    {
      id: 'f7',
      patientId,
      kind: 'document',
      filename: 'Treatment-Plan-2026.pdf',
      tag: 'documents',
      createdAt: '2026-02-18T09:00:00Z',
      starred: false,
      mimeType: 'application/pdf',
    },
    {
      id: 'f8',
      patientId,
      kind: 'document',
      filename: 'Informed-Consent-Extraction.pdf',
      tag: 'consent',
      createdAt: '2026-02-17T15:30:00Z',
      starred: true,
      mimeType: 'application/pdf',
    },
    {
      id: 'f9',
      patientId,
      kind: 'document',
      filename: 'Lab-Report-CBC.pdf',
      tag: 'lab',
      createdAt: '2026-02-16T08:00:00Z',
      starred: false,
      mimeType: 'application/pdf',
    },
    {
      id: 'f10',
      patientId,
      kind: 'document',
      filename: 'Referral-Letter-Ortho.docx',
      tag: 'other',
      createdAt: '2026-02-15T13:45:00Z',
      starred: false,
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
  ];
}

// ─── Dummy notes ───

export function getDummyNotes(patientId: string): PatientNoteItem[] {
  return [
    {
      id: 'n1',
      patientId,
      title: 'Initial Assessment',
      body: 'Patient presents with mild gingivitis in the lower anterior region. Recommended professional cleaning and improved home care routine. Patient reports sensitivity to cold on tooth #14.',
      updatedAt: '2026-02-18T10:45:00Z',
    },
    {
      id: 'n2',
      patientId,
      title: 'Follow-up: Crown Prep #19',
      body: 'Prepared tooth #19 for PFM crown. Impression taken with polyvinyl siloxane. Temporary crown placed. Patient tolerated procedure well. Return in 2 weeks for permanent crown.',
      updatedAt: '2026-02-15T14:20:00Z',
    },
    {
      id: 'n3',
      patientId,
      title: 'Orthodontic Consultation Notes',
      body: 'Discussed treatment options for mild crowding in lower arch. Patient interested in clear aligners. Referred to Dr. Martinez for evaluation and treatment planning.',
      updatedAt: '2026-02-10T09:30:00Z',
    },
    {
      id: 'n4',
      patientId,
      title: 'Post-Op: Extraction #32',
      body: 'Patient returned for post-operative check after wisdom tooth extraction. Healing well. No signs of dry socket. Advised to continue soft diet for 2 more days.',
      updatedAt: '2026-02-05T11:15:00Z',
    },
  ];
}

// ─── Dummy forms ───

export function getDummyForms(patientId: string): PatientFormItem[] {
  return [
    {
      id: 'fm1',
      patientId,
      name: 'Informed Consent — Tooth Extraction',
      tag: 'consent',
      createdAt: '2026-02-18T09:15:00Z',
    },
    {
      id: 'fm2',
      patientId,
      name: 'Informed Consent — Root Canal',
      tag: 'consent',
      createdAt: '2026-02-15T10:00:00Z',
    },
    {
      id: 'fm3',
      patientId,
      name: 'CBC Lab Order',
      tag: 'lab',
      createdAt: '2026-02-14T08:30:00Z',
    },
    {
      id: 'fm4',
      patientId,
      name: 'Orthodontic Referral Form',
      tag: 'other',
      createdAt: '2026-02-10T14:00:00Z',
    },
    {
      id: 'fm5',
      patientId,
      name: 'Dental Clearance Form',
      tag: 'other',
      createdAt: '2026-02-08T11:30:00Z',
    },
  ];
}

// ─── Invoice types ───

export type InvoiceStatus = 'draft' | 'issued' | 'paid';

export interface InvoiceItem {
  id: string;
  serviceName: string;
  qty: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
}

export interface Invoice {
  id: string;
  invoiceNo: string;
  patientId: string;
  patientName: string;
  dateISO: string;
  status: InvoiceStatus;
  notes?: string;
  items: InvoiceItem[];
  createdAtISO: string;
  updatedAtISO: string;
}

// ─── Invoice computation helpers ───

export function rowTaxAmount(item: InvoiceItem): number {
  const base = item.qty * item.unitPrice - item.discount;
  return base * (item.taxRate / 100);
}

export function rowTotal(item: InvoiceItem): number {
  const base = item.qty * item.unitPrice - item.discount;
  return base + base * (item.taxRate / 100);
}

export function invoiceSummary(items: InvoiceItem[]) {
  const subtotal = items.reduce((s, i) => s + i.qty * i.unitPrice, 0);
  const discountTotal = items.reduce((s, i) => s + i.discount, 0);
  const taxTotal = items.reduce((s, i) => s + rowTaxAmount(i), 0);
  const grandTotal = subtotal - discountTotal + taxTotal;
  return { subtotal, discountTotal, taxTotal, grandTotal };
}

export function formatCurrency(amount: number): string {
  return `₱${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Dummy invoices ───

export function getDummyInvoices(patientId: string, patientName: string): Invoice[] {
  return [
    {
      id: 'inv1',
      invoiceNo: 'INV-00021',
      patientId,
      patientName,
      dateISO: '2026-03-16',
      status: 'draft',
      notes: '',
      items: [
        {
          id: 'ii1',
          serviceName: 'Consultation',
          qty: 1,
          unitPrice: 1500,
          discount: 0,
          taxRate: 0,
        },
      ],
      createdAtISO: '2026-03-16T09:00:00Z',
      updatedAtISO: '2026-03-16T09:00:00Z',
    },
    {
      id: 'inv2',
      invoiceNo: 'INV-00020',
      patientId,
      patientName,
      dateISO: '2026-03-10',
      status: 'paid',
      notes: 'Paid via GCash',
      items: [
        {
          id: 'ii2',
          serviceName: 'Consultation',
          qty: 1,
          unitPrice: 1500,
          discount: 0,
          taxRate: 0,
        },
        {
          id: 'ii3',
          serviceName: 'Dental Cleaning',
          qty: 1,
          unitPrice: 500,
          discount: 0,
          taxRate: 0,
        },
      ],
      createdAtISO: '2026-03-10T10:30:00Z',
      updatedAtISO: '2026-03-10T11:00:00Z',
    },
    {
      id: 'inv3',
      invoiceNo: 'INV-00019',
      patientId,
      patientName,
      dateISO: '2026-02-18',
      status: 'issued',
      notes: '',
      items: [
        {
          id: 'ii4',
          serviceName: 'Consultation',
          qty: 1,
          unitPrice: 1250,
          discount: 0,
          taxRate: 0,
        },
      ],
      createdAtISO: '2026-02-18T14:00:00Z',
      updatedAtISO: '2026-02-18T14:00:00Z',
    },
  ];
}

// ─── Helpers ───

const TAG_LABELS: Record<string, string> = {
  images: 'image',
  documents: 'document',
  consent: 'consent',
  lab: 'lab',
  before_after: 'before',
  other: 'other',
};

export function tagLabel(tag: string): string {
  return TAG_LABELS[tag] ?? tag;
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h12}:${m} ${ampm}`;
}

export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
